<!-- MediaManagement.vue -->
<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import type { Component } from 'vue'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import Paginator from 'primevue/paginator'
import { useToast } from 'primevue/usetoast'
import AdminLayout from '@/components/layout/AdminLayout.vue'
import CardComponent from '@/components/cards/CardComponent.vue'
import TextInputDefault from '@/components/forms/TextInputDefault.vue'
import BaseButton from '@/components/Buttons/BaseButton.vue'
import ModalDefault from '@/components/Modals/ModalDefault.vue'
import HeadingDefault from '@/components/Heading/HeadingOne.vue'
import CheckBoxDefault from '@/components/forms/Checkboxes/CheckBoxDefault.vue'
import mediaService from '@/services/mediaService'
import type { MediaDocument } from '@/types/media'
import EmptyState from '@/components/common/EmptyState.vue'
import LoaderDefault from '@/components/common/LoaderDefault.vue'
import { PDFDocument, type PDFPage } from 'pdf-lib'
import {
  DownloadIcon,
  DeleteIcon,
  EyeIcon,
  FileWaveIcon,
  UploadIcon,
  FileExcleIcon,
  ImageIcon,
  FilePdfIcon,
  FileWordIcon,
  MergeIcon,
} from '@/icons/'

// Import modal content components
import UploadDocumentModalContent from '../Modals/UploadDocumentModalContent.vue'
import MergeDocumentsModalContent from '../Modals/MergeDocumentsModalContent.vue'
import DeleteDocumentModalContent from '../Modals/DeleteDocumentModalContent.vue'

// Extend MediaDocument to include selected property
interface MediaDocumentWithSelection extends MediaDocument {
  selected?: boolean
}

const toast = useToast()

// Reactive state
const documents = ref<MediaDocumentWithSelection[]>([])
const loading = ref(false)
const error = ref<string | null>(null)
const searchFilter = ref('')

// Modal states
const showUploadModal = ref(false)
const showDeleteModal = ref(false)
const showMergeModal = ref(false)

// Modal refs
const uploadModalRef = ref<InstanceType<typeof UploadDocumentModalContent> | null>(null)
const mergeModalRef = ref<InstanceType<typeof MergeDocumentsModalContent> | null>(null)
const deleteModalRef = ref<InstanceType<typeof DeleteDocumentModalContent> | null>(null)

// Modal loading states
const uploadingDocument = ref(false)
const deletingDocument = ref(false)
const mergingPdf = ref(false)
const mergeStep = ref('')

// Document management
const documentToDelete = ref<MediaDocumentWithSelection | null>(null)
const selectedDocuments = ref<MediaDocumentWithSelection[]>([])

// Pagination
const currentPage = ref(1)
const totalRecords = ref(0)
const rowsPerPage = ref(10)

// File size limit
const MAX_FILE_SIZE = 50 * 1024 * 1024 // 50MB
const MAX_FILE_SIZE_MB = MAX_FILE_SIZE / 1024 / 1024

// Computed
const allSelected = computed({
  get: () => documents.value.length > 0 && documents.value.every(doc => doc.selected === true),
  set: (value: boolean) => {
    documents.value.forEach(doc => {
      doc.selected = value
    })
    selectedDocuments.value = value ? [...documents.value] : []
  }
})

const hasSelectedDocuments = computed(() => selectedDocuments.value.length > 1)
const mergeableSelectedDocs = computed(() => selectedDocuments.value.filter(doc => isMergeableFile(doc)))
const hasUnmergeableFiles = computed(() => selectedDocuments.value.some(doc => !isMergeableFile(doc)))
const unmergeableFileNames = computed(() =>
  selectedDocuments.value.filter(doc => !isMergeableFile(doc)).map(doc => doc.title).join(', ')
)

const canMerge = computed(() => mergeableSelectedDocs.value.length >= 2 && !mergingPdf.value)

// File helpers
const isMergeableFile = (doc: MediaDocumentWithSelection): boolean => {
  const type = doc.fileType?.toLowerCase() || ''
  const title = doc.title?.toLowerCase() || ''
  if (type.includes('pdf') || title.includes('.pdf') || title.endsWith('pdf')) return true
  if (['jpeg', 'jpg', 'png', 'gif', 'bmp', 'webp', 'tiff', 'image/'].some(t => type.includes(t))) return true
  return ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.tiff']
    .some(ext => title.includes(ext) || title.endsWith(ext.replace('.', '')))
}

const getFileIcon = (fileType: string): Component => {
  const type = fileType?.toLowerCase() || ''
  if (type.includes('pdf')) return FilePdfIcon
  if (type.includes('word') || type.includes('doc') || type.includes('msword') || type.includes('document')) return FileWordIcon
  if (type.includes('excel') || type.includes('sheet') || type.includes('xls') || type.includes('spreadsheet') || type.includes('ms-excel')) return FileExcleIcon
  if (type.includes('jpg') || type.includes('jpeg') || type.includes('png') || type.includes('gif') || type.includes('bmp') || type.includes('image')) return ImageIcon
  return FileWaveIcon
}

const formatDate = (dateString: string): string => {
  if (!dateString) return ''
  try {
    const date = new Date(dateString)
    if (isNaN(date.getTime())) return ''
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
  } catch { return '' }
}

const formatFileSize = (size: string | number): string => {
  if (typeof size === 'string') return formatBytes(parseInt(size, 10))
  return formatBytes(size)
}

const formatBytes = (bytes: number, decimals = 2): string => {
  if (bytes === 0) return '0 Bytes'
  const k = 1024
  const dm = decimals < 0 ? 0 : decimals
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i]
}

// Handle checkbox selection
const handleCheckboxChange = (doc: MediaDocumentWithSelection, isChecked: boolean) => {
  doc.selected = isChecked
  if (isChecked) {
    selectedDocuments.value.push(doc)
  } else {
    selectedDocuments.value = selectedDocuments.value.filter(d => d.id !== doc.id)
  }
}

// Fetch documents
const fetchDocuments = async () => {
  try {
    loading.value = true
    error.value = null

    const params: Record<string, string | number> = {
      page: currentPage.value,
      limit: rowsPerPage.value,
    }
    if (searchFilter.value) {
      params.search = searchFilter.value
    }

    const response = await mediaService.getDocuments(params)

    if (Array.isArray(response)) {
      documents.value = response.map(doc => ({ ...doc, selected: false }))
      totalRecords.value = response.length
    } else if (response.data && Array.isArray(response.data)) {
      documents.value = response.data.map(doc => ({ ...doc, selected: false }))
      totalRecords.value = response.data.length
    } else {
      documents.value = []
      totalRecords.value = 0
    }

    selectedDocuments.value = []
  } catch (err: unknown) {
    console.error('Error loading documents:', err)
    error.value = err instanceof Error ? err.message : 'Failed to load documents.'
    toast.add({ severity: 'error', summary: 'Error', detail: error.value, life: 5000 })
  } finally {
    loading.value = false
  }
}

// Upload handlers
const handleUploadSubmit = async (data: { title: string; description: string; file: File }) => {
  uploadingDocument.value = true
  try {
    await mediaService.uploadDocument({
      title: data.title,
      description: data.description || undefined,
      file: data.file,
    })

    toast.add({ severity: 'success', summary: 'Success', detail: 'Document uploaded successfully!', life: 3000 })
    await fetchDocuments()
    showUploadModal.value = false
  } catch (err: unknown) {
    console.error('Error uploading document:', err)
    toast.add({ severity: 'error', summary: 'Upload Failed', detail: err instanceof Error ? err.message : 'Failed to upload document.', life: 5000 })
  } finally {
    uploadingDocument.value = false
  }
}

const closeUploadModal = () => {
  showUploadModal.value = false
  if (uploadModalRef.value) {
    uploadModalRef.value.resetForm()
  }
}

// Merge handlers
const handleMergeSubmit = async () => {
  if (!canMerge.value) return

  mergingPdf.value = true
  const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19)
  const filename = `Merged_Documents_${timestamp}.pdf`

  try {
    const mergedPdf = await PDFDocument.create()
    const docsToMerge = mergeableSelectedDocs.value
    let processedItems = 0

    for (let i = 0; i < docsToMerge.length; i++) {
      const doc = docsToMerge[i]
      mergeStep.value = `Downloading ${i + 1}/${docsToMerge.length}: ${doc.title}...`

      try {
        const blob = await mediaService.downloadDocument(doc.id)
        const arrayBuffer = await blob.arrayBuffer()
        const fileType = (doc.fileType || '').toLowerCase()
        const title = (doc.title || '').toLowerCase()

        mergeStep.value = `Processing ${i + 1}/${docsToMerge.length}: ${doc.title}...`

        if (fileType.includes('pdf') || title.endsWith('.pdf')) {
          const srcDoc = await PDFDocument.load(arrayBuffer, { ignoreEncryption: true })
          const pages = await mergedPdf.copyPages(srcDoc, srcDoc.getPageIndices())
          pages.forEach((p: PDFPage) => mergedPdf.addPage(p))
        } else if (fileType.includes('jpeg') || fileType.includes('jpg') || title.endsWith('.jpg') || title.endsWith('.jpeg')) {
          const img = await mergedPdf.embedJpg(arrayBuffer)
          const page = mergedPdf.addPage([595.28, 841.89])
          const { width, height } = img.scaleToFit(595.28, 841.89)
          page.drawImage(img, { x: (595.28 - width) / 2, y: (841.89 - height) / 2, width, height })
        } else if (fileType.includes('png') || title.endsWith('.png')) {
          const img = await mergedPdf.embedPng(arrayBuffer)
          const page = mergedPdf.addPage([595.28, 841.89])
          const { width, height } = img.scaleToFit(595.28, 841.89)
          page.drawImage(img, { x: (595.28 - width) / 2, y: (841.89 - height) / 2, width, height })
        } else if (fileType.includes('gif') || title.endsWith('.gif')) {
          toast.add({ severity: 'warn', summary: 'Skipped', detail: `"${doc.title}" GIF format not supported for merging`, life: 3000 })
          continue
        } else {
          toast.add({ severity: 'warn', summary: 'Skipped', detail: `"${doc.title}" format not supported for merging`, life: 3000 })
          continue
        }

        processedItems++
        mergeStep.value = `Processed ${processedItems}/${docsToMerge.length} items...`
      } catch (err) {
        console.error(`Failed to process "${doc.title}":`, err)
        toast.add({ severity: 'error', summary: 'Error', detail: `Could not include "${doc.title}"`, life: 3000 })
      }
    }

    if (processedItems === 0) {
      toast.add({ severity: 'error', summary: 'Merge Failed', detail: 'No documents could be merged', life: 5000 })
      return
    }

    mergeStep.value = 'Finalizing PDF...'
    const mergedBytes = await mergedPdf.save()
    const mergedBlob = new Blob([new Uint8Array(mergedBytes)], { type: 'application/pdf' })

    if (mergedBlob.size > MAX_FILE_SIZE) {
      toast.add({
        severity: 'error',
        summary: 'File Too Large',
        detail: `Merged PDF is too large (${(mergedBlob.size / 1024 / 1024).toFixed(2)}MB). Maximum is ${MAX_FILE_SIZE_MB}MB.`,
        life: 5000
      })
      return
    }

    const url = window.URL.createObjectURL(mergedBlob)
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    setTimeout(() => {
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    }, 1000)

    toast.add({
      severity: 'success',
      summary: 'Success',
      detail: `${processedItems} document(s) merged successfully!`,
      life: 5000
    })

    showMergeModal.value = false
    selectedDocuments.value = []
    documents.value.forEach(doc => doc.selected = false)

  } catch (error) {
    console.error('Merge error:', error)
    toast.add({
      severity: 'error',
      summary: 'Merge Failed',
      detail: error instanceof Error ? error.message : 'Failed to merge documents',
      life: 5000
    })
  } finally {
    mergingPdf.value = false
    mergeStep.value = ''
  }
}

const closeMergeModal = () => {
  showMergeModal.value = false
  selectedDocuments.value = []
  documents.value.forEach(doc => doc.selected = false)
}

// Delete handlers
const openDeleteModal = (document: MediaDocumentWithSelection) => {
  documentToDelete.value = document
  showDeleteModal.value = true
}

const handleDeleteSubmit = async () => {
  if (!documentToDelete.value) return

  deletingDocument.value = true
  try {
    await mediaService.deleteDocument(documentToDelete.value.id)
    toast.add({ severity: 'success', summary: 'Success', detail: 'Document deleted successfully!', life: 3000 })
    await fetchDocuments()
    showDeleteModal.value = false
    documentToDelete.value = null
  } catch (err: unknown) {
    toast.add({ severity: 'error', summary: 'Delete Failed', detail: err instanceof Error ? err.message : 'Failed to delete document.', life: 5000 })
  } finally {
    deletingDocument.value = false
  }
}

const closeDeleteModal = () => {
  showDeleteModal.value = false
  documentToDelete.value = null
}

const viewDocument = (mediaDoc: MediaDocumentWithSelection) => {
  mediaService.viewDocument(mediaDoc.id)
}

const downloadDocument = (mediaDoc: MediaDocumentWithSelection) => {
  mediaService.downloadDocumentDirect(mediaDoc)
  toast.add({ severity: 'success', summary: 'Download Started', detail: `Downloading ${mediaDoc.title}`, life: 3000 })
}

let searchTimeout: number
const handleSearch = () => {
  clearTimeout(searchTimeout)
  searchTimeout = window.setTimeout(() => {
    currentPage.value = 1
    fetchDocuments()
  }, 500)
}

const onPageChange = (event: { page: number; rows?: number }) => {
  currentPage.value = event.page + 1
  if (event.rows) rowsPerPage.value = event.rows
  fetchDocuments()
}

const totalStorageUsed = computed(() =>
  documents.value.reduce((total, doc) => {
    const size = doc.fileSize
    if (typeof size === 'string') {
      const n = parseInt(size, 10)
      if (!isNaN(n)) return total + n
    }
    return total + (typeof size === 'number' ? size : 0)
  }, 0)
)

const formattedStorageUsed = computed(() => formatBytes(totalStorageUsed.value))

onMounted(() => { fetchDocuments() })
</script>

<template>
  <AdminLayout>
    <HeadingDefault heading="Media Management" />

    <CardComponent
      className="max-w-full"
      footerClass="justify-between items-center"
      title="Document Library"
      :showHeaderBorder="true"
      :showHeaderPaddingBottom="true"
    >
      <template #header>
        <div class="flex flex-col md:flex-row md:items-center justify-between gap-2">
          <div class="flex flex-wrap gap-2">
            <TextInputDefault
              v-model="searchFilter"
              placeholder="Search documents..."
              id="searchdocuments"
              name="searchdocuments"
              type="text"
              className="w-48"
              size="xss"
              @update:modelValue="handleSearch"
            />
          </div>
          <div class="flex gap-2">
            <BaseButton text="Clear Filter" size="xss" variant="ghost" @click="searchFilter = ''; fetchDocuments()" :disabled="!searchFilter" />
            <BaseButton
              v-if="hasSelectedDocuments"
              text="Merge Selected"
              size="xss"
              variant="primary"
              :icon="MergeIcon"
              @click="showMergeModal = true"
              :disabled="!canMerge"
            />
            <BaseButton text="Upload Document" size="xss" @click="showUploadModal = true" :icon="UploadIcon" />
          </div>
        </div>
      </template>

      <template #body>
        <LoaderDefault v-if="loading" title="Loading documents..." />

        <div v-else-if="error" class="w-full flex flex-col items-center text-center py-12 gap-4">
          <div class="w-16 h-16 text-red-400">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" class="w-full h-full">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.282 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <div class="space-y-2">
            <p class="text-lg font-medium text-gray-700">Error Loading Documents</p>
            <p class="text-sm text-gray-500">{{ error }}</p>
          </div>
          <BaseButton text="Retry" size="sm" @click="fetchDocuments" class="w-32" />
        </div>

        <EmptyState
          v-else-if="documents.length === 0"
          :icon="FileWaveIcon"
          title="No documents found"
          description="No documents match your search"
          hint="No documents uploaded yet. Click Upload Document to add your first document"
          action-text="Upload First Document"
          :actionIcon="UploadIcon"
        />

        <DataTable
          v-else
          :value="documents"
          class="w-full"
          sortMode="multiple"
          scrollable
          scrollHeight="500px"
          resizableColumns
          columnResizeMode="fit"
          showGridlines
        >
          <Column
            headerStyle="width: 48px; position: sticky; left: 0; z-index: 30;"
            bodyStyle="position: sticky; left: 0; z-index: 25;"
            frozen
          >
            <template #header>
              <CheckBoxDefault v-model="allSelected" />
            </template>
            <template #body="{ data }">
              <CheckBoxDefault
                :modelValue="data.selected"
                @update:modelValue="(val: boolean) => handleCheckboxChange(data, val)"
              />
            </template>
          </Column>

          <Column sortable field="title" header="Document">
            <template #body="{ data }">
              <div class="flex items-center gap-3">
                <component :is="getFileIcon(data.fileType)" class="w-8 h-8 mt-1" />
                <div class="flex flex-col">
                  <span class="font-medium hover:text-blue-600 cursor-pointer" @click="viewDocument(data)">
                    {{ data.title }}
                  </span>
                  <span v-if="data.description" class="text-xs text-gray-400 mt-1 line-clamp-1">
                    {{ data.description }}
                  </span>
                </div>
              </div>
            </template>
          </Column>

          <Column sortable field="fileSize" header="Size">
            <template #body="{ data }">
              <div class="text-sm font-medium">{{ formatFileSize(data.fileSize) }}</div>
            </template>
          </Column>

          <Column sortable field="uploadedAt" header="Uploaded On">
            <template #body="{ data }">
              <div class="text-sm font-medium">{{ formatDate(data.uploadedAt) }}</div>
            </template>
          </Column>

          <Column field="action" header="Actions" headerStyle="width: 100px; position: sticky; right: 0; z-index: 30;" bodyStyle="position: sticky; right: 0; z-index: 25;">
            <template #body="{ data }">
              <div class="flex justify-end gap-2">
                <BaseButton size="xss" :aria-label="`view ${data.title}`" @click="viewDocument(data)" variant="secondary" tooltip="View Document" :icon="EyeIcon" />
                <BaseButton size="xss" :aria-label="`download ${data.title}`" @click="downloadDocument(data)" variant="secondary" tooltip="Download Document" :icon="DownloadIcon" />
                <BaseButton size="xss" :aria-label="`delete ${data.title}`" @click="openDeleteModal(data)" variant="danger" tooltip="Delete File" :icon="DeleteIcon" />
              </div>
            </template>
          </Column>
        </DataTable>
      </template>

      <template #footer v-if="!loading && !error && documents.length > 0">
        <div class="text-xs text-gray-500">
          Showing {{ documents.length }} of {{ totalRecords }} documents
          <span class="ml-2">•</span>
          <span class="ml-2">Storage used: {{ formattedStorageUsed }}</span>
          <span v-if="hasSelectedDocuments" class="ml-2 text-blue-600">
            ({{ selectedDocuments.length }} selected)
          </span>
        </div>

        <Paginator
          :first="(currentPage - 1) * rowsPerPage"
          :rows="rowsPerPage"
          :totalRecords="totalRecords"
          :rowsPerPageOptions="[10, 25, 50]"
          @page="onPageChange"
          class="border-0"
        />
      </template>
    </CardComponent>
  </AdminLayout>

  <!-- Upload Modal -->
  <ModalDefault
    v-model="showUploadModal"
    title="Upload New Document"
    width="max-w-lg"
    classBody="my-4"
    @close="closeUploadModal"
    :loading="uploadingDocument"
  >
    <UploadDocumentModalContent
      ref="uploadModalRef"
      @upload="handleUploadSubmit"
    />
    <template #footer>
      <div class="flex justify-end gap-3">
        <BaseButton
          text="Cancel"
          variant="secondary"
          @click="closeUploadModal"
          :disabled="uploadingDocument"
        />
        <BaseButton
          text="Upload Document"
          variant="primary"
          @click="uploadModalRef?.submitForm()"
          :disabled="uploadingDocument"
          :loading="uploadingDocument"
          loading-text="Uploading..."
        />
      </div>
    </template>
  </ModalDefault>

  <!-- Merge Modal -->
  <ModalDefault
    v-model="showMergeModal"
    title="Merge Documents"
    width="max-w-lg"
    classBody="my-4"
    @close="closeMergeModal"
    :loading="mergingPdf"
  >
    <MergeDocumentsModalContent
      ref="mergeModalRef"
      :selected-documents="selectedDocuments"
      :merging-pdf="mergingPdf"
      :merge-step="mergeStep"
      :can-merge="canMerge"
      :unmergeable-file-names="unmergeableFileNames"
      :has-unmergeable-files="hasUnmergeableFiles"
      :mergeable-selected-docs="mergeableSelectedDocs"
      :MAX_FILE_SIZE_MB="MAX_FILE_SIZE_MB"
      @merge="handleMergeSubmit"
    />
    <template #footer>
      <div class="flex justify-end gap-3">
        <BaseButton
          text="Cancel"
          variant="secondary"
          @click="closeMergeModal"
          :disabled="mergingPdf"
        />
        <BaseButton
          text="Merge & Download"
          variant="primary"
          :icon="MergeIcon"
          :loading="mergingPdf"
          :disabled="!canMerge"
          loading-text="Merging..."
          @click="mergeModalRef?.submitForm()"
        />
      </div>
    </template>
  </ModalDefault>

  <!-- Delete Modal -->
  <ModalDefault
    v-model="showDeleteModal"
    title="Delete Document"
    width="max-w-md"
    classBody="my-4"
    @close="closeDeleteModal"
    :loading="deletingDocument"
  >
    <DeleteDocumentModalContent
      ref="deleteModalRef"
      :document="documentToDelete"
      @confirm="handleDeleteSubmit"
    />
    <template #footer>
      <div class="flex justify-end gap-3">
        <BaseButton
          text="Cancel"
          variant="secondary"
          @click="closeDeleteModal"
          :disabled="deletingDocument"
        />
        <BaseButton
          text="Delete Document"
          variant="danger"
          :loading="deletingDocument"
          loading-text="Deleting..."
          @click="deleteModalRef?.submitForm()"
        />
      </div>
    </template>
  </ModalDefault>
</template>
